import type { BrandColors, BrandFonts } from "@ease/page-builder";

export type WorkspaceTab = "sites" | "templates" | "pages";

export interface SiteRecord {
  id: string;
  name: string;
  slug: string;
  siteType: string;
  status: "draft" | "published" | "archived";
  homePageId: string | null;
  globalTheme: {
    colors?: {
      brand?: string;
      accent?: string;
      background?: string;
      text?: string;
    };
    fonts?: {
      heading?: { family?: string };
      body?: { family?: string };
    };
  };
  headerContent: Record<string, unknown> | null;
  footerContent: Record<string, unknown> | null;
  updatedAt: string;
  createdAt: string;
}

export interface BrandKitSummary {
  logoUrl: string | null;
  colors: BrandColors;
  fonts: BrandFonts;
  brandVoice: string | null;
  tagline: string | null;
}

export function normalizeWorkspaceTab(value: string | null): WorkspaceTab {
  if (value === "templates" || value === "pages") {
    return value;
  }

  return "sites";
}

export function normalizeSiteSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
}

export function formatThemeStatus(theme: SiteRecord["globalTheme"]): string {
  const colorSummary = theme.colors?.brand
    ? `Brand ${theme.colors.brand}`
    : "No global brand color yet";
  const fontSummary = theme.fonts?.heading?.family
    ? `Heading ${theme.fonts.heading.family}`
    : "heading font not set";

  return `${colorSummary}; ${fontSummary}.`;
}

function hasPuckContent(value: Record<string, unknown> | null): boolean {
  if (!value) return false;
  const content = value.content;
  return Array.isArray(content) && content.length > 0;
}

export function formatShellStatus(site: Pick<SiteRecord, "headerContent" | "footerContent">): string {
  const headerStatus = hasPuckContent(site.headerContent) ? "Header configured" : "Header missing";
  const footerStatus = hasPuckContent(site.footerContent) ? "footer configured" : "footer missing";
  return `${headerStatus}; ${footerStatus}.`;
}

export function formatBrandStatus(brand: BrandKitSummary): string {
  const colorSummary = brand.colors.primary ? `Primary ${brand.colors.primary}` : "No primary color";
  const fontSummary = brand.fonts.headings?.family
    ? `heading font ${brand.fonts.headings.family}`
    : "fonts not configured";
  const voiceSummary = brand.brandVoice ? `voice ${brand.brandVoice}` : "voice not set";

  return `${colorSummary}, ${fontSummary}, ${voiceSummary}.`;
}
